import { computed, readonly, ref } from 'vue'

export interface ConfirmDialogOptions {
  title?: string
  message: string
  detail?: string
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'default' | 'danger' | 'primary'
}

interface ConfirmDialogState extends Required<
  Pick<ConfirmDialogOptions, 'title' | 'message' | 'confirmLabel' | 'cancelLabel' | 'tone'>
> {
  detail: string
  open: boolean
}

type Resolver = (value: boolean) => void

const state = ref<ConfirmDialogState>({
  open: false,
  title: '확인',
  message: '',
  detail: '',
  confirmLabel: '확인',
  cancelLabel: '취소',
  tone: 'default',
})

let resolver: Resolver | null = null

function close(result: boolean) {
  state.value = { ...state.value, open: false }
  const resolve = resolver
  resolver = null
  resolve?.(result)
}

/** 앱 전역 확인 모달. true=확인, false=취소 */
export function confirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  if (resolver) {
    resolver(false)
    resolver = null
  }

  state.value = {
    open: true,
    title: options.title || '확인',
    message: options.message,
    detail: options.detail || '',
    confirmLabel: options.confirmLabel || '확인',
    cancelLabel: options.cancelLabel || '취소',
    tone: options.tone || 'default',
  }

  return new Promise<boolean>((resolve) => {
    resolver = resolve
  })
}

export function useConfirmDialogHost() {
  const open = computed(() => state.value.open)
  const title = computed(() => state.value.title)
  const message = computed(() => state.value.message)
  const detail = computed(() => state.value.detail)
  const confirmLabel = computed(() => state.value.confirmLabel)
  const cancelLabel = computed(() => state.value.cancelLabel)
  const tone = computed(() => state.value.tone)

  function onConfirm() {
    close(true)
  }

  function onCancel() {
    close(false)
  }

  return {
    open: readonly(open),
    title,
    message,
    detail,
    confirmLabel,
    cancelLabel,
    tone,
    onConfirm,
    onCancel,
  }
}
